/**
 * PostHog Analytics Provider
 *
 * PostHog-specific implementation of the AnalyticsProvider interface.
 * This file contains all PostHog-specific logic, making it easy to swap
 * for a different analytics provider if needed.
 */

import posthog from "posthog-js";
import type {
  AnalyticsProvider,
  AnalyticsEventName,
  AnalyticsEventPropertiesMap,
} from "../types/analytics";
import { createLogger } from "../utils/logger";

// Create logger for analytics
const logger = createLogger("Analytics");

/**
 * PostHog implementation of the AnalyticsProvider interface
 */
export class PostHogProvider implements AnalyticsProvider {
  private initialized = false;

  /**
   * Initialize PostHog with configuration from environment variables
   */
  initialize(): void {
    if (!this.isEnabled()) {
      logger.debug("Disabled in development mode");
      return;
    }

    // Posthog project keys are not secret - they are safe to expose in frontend code
    // because they are write-only and rate-limited.
    // See: https://posthog.com/docs/privacy
    const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;

    if (!apiKey) {
      logger.debug("Not initialized: missing API key");
      return;
    }

    try {
      posthog.init(apiKey, {
        // See PostHogConfig docs for all options:
        api_host: "https://us.i.posthog.com", // Main US server - can make this variable later for cross-region deploys but latency is not high impact for analytics
        defaults: "2025-11-30", // Essentially PostHog version - pinning for stability
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
        },
        // Respect user's Do Not Track header
        respect_dnt: true,
      });

      this.initialized = true;
      logger.debug("Initialized successfully");
    } catch (error) {
      logger.error("Failed to initialize", { error });
    }
  }

  /**
   * Track an analytics event
   *
   * @param event - The event name
   * @param properties - The event properties
   */
  track<E extends AnalyticsEventName>(event: E, properties: AnalyticsEventPropertiesMap[E]): void {
    if (!this.isEnabled() || !this.initialized) {
      return;
    }

    try {
      posthog.capture(event, properties);
    } catch (error) {
      // Never crash the app due to analytics errors
      logger.error("Failed to track event", { event, error });
    }
  }

  /**
   * Identify a user
   *
   * Best Practice: Call this when a user signs up or logs in. This links all
   * past anonymous (e.g., logged out) events to the identified user.
   * Use email as the distinct_id for consistency across sessions.
   *
   * @param userId - Unique identifier for the user (email recommended)
   * @param traits - User properties to set
   */
  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.isEnabled() || !this.initialized) {
      return;
    }

    try {
      posthog.identify(userId, traits);
    } catch (error) {
      logger.error("Failed to identify user", { userId, error });
    }
  }

  /**
   * Reset user identification
   *
   * Best Practice: Call this when a user logs out to unlink future events
   * from that user. This is mainly for shared devices.
   */
  reset(): void {
    if (!this.isEnabled() || !this.initialized) {
      return;
    }

    try {
      posthog.reset();
    } catch (error) {
      logger.error("Failed to reset user", { error });
    }
  }

  /**
   * Check if analytics is enabled
   *
   * Disabled in:
   * - Local
   * - Dev
   *
   * Enabled in:
   * - Staging
   * - Prod
   *
   * Can be overridden with VITE_ANALYTICS_OVERRIDE=true for local testing
   */
  isEnabled(): boolean {
    if (import.meta.env.VITE_ANALYTICS_OVERRIDE === "true") {
      return true;
    }

    return !(import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === "dev");
  }
}

/**
 * Singleton instance of the PostHog provider
 */
export const posthogProvider = new PostHogProvider();
